import { isSettingsDeferredOwnerPath } from './settings-deferred-owner';
import { mainOwnerSettingsLeafAllowedForRole } from './settings-owner-main-parity';
import { resolveSettingsAccess } from './settings-resolver';
import { flattenSettingsIa, type SettingsLeaf } from '@/components/nav-v2/settings/settings-ia';

export const SETTINGS_DELEGATION_PAGE_KEY = 'settings:delegation';
export const SETTINGS_DELEGATION_PREF_TYPE = 'user_pref' as const;

/** Roles a tenant admin may grant or restrict per cluster (never includes platform admins). */
export const DELEGATABLE_ROLES_BY_CLUSTER: Readonly<
  Record<string, readonly { value: string; label: string }[]>
> = {
  organisation: [
    { value: 'FACILITY_MANAGER', label: 'Facility Manager' },
    { value: 'REGION_MANAGER', label: 'Region Manager' },
  ],
  people: [],
  'care-config': [
    { value: 'QUALITY_OFFICER', label: 'Quality Officer' },
    { value: 'CLINICAL_LEAD', label: 'Clinical lead' },
  ],
  notifications: [
    { value: 'COORDINATOR', label: 'Coordinator' },
    { value: 'FACILITY_MANAGER', label: 'Facility Manager' },
  ],
  integrations: [{ value: 'FINANCE_OFFICER', label: 'Finance Officer' }],
  security: [{ value: 'COMPLIANCE_OFFICER', label: 'Compliance Officer' }],
  billing: [{ value: 'FINANCE_OFFICER', label: 'Finance Officer' }],
};

export type SettingsDelegationPreference = {
  /** Roles explicitly allowed into a cluster beyond IA defaults. */
  grantedRolesByCluster: Partial<Record<string, string[]>>;
  /** Roles explicitly blocked from a cluster even when IA defaults would allow. */
  deniedRolesByCluster: Partial<Record<string, string[]>>;
};

const ADMIN_ROLES = new Set(['SYSTEM_ADMIN', 'TENANT_ADMIN']);

function isOperatorOnlyCohort(roles: readonly string[]): boolean {
  return roles.length === 1 && roles[0] === 'SYSTEM_ADMIN';
}

export function emptySettingsDelegationPreference(): SettingsDelegationPreference {
  return { grantedRolesByCluster: {}, deniedRolesByCluster: {} };
}

export function parseSettingsDelegationPreference(
  visibleKeys: Record<string, unknown> | null | undefined,
): SettingsDelegationPreference {
  const raw = visibleKeys ?? {};
  const granted =
    (raw.grantedRolesByCluster as Partial<Record<string, string[]>> | undefined) ?? {};
  const denied = (raw.deniedRolesByCluster as Partial<Record<string, string[]>> | undefined) ?? {};
  return {
    grantedRolesByCluster: sanitiseDelegationMap(granted),
    deniedRolesByCluster: sanitiseDelegationMap(denied),
  };
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isWellFormedRoleMap(value: unknown): boolean {
  if (value === undefined) return true;
  if (!isPlainRecord(value)) return false;
  return Object.values(value).every(
    (roles) => Array.isArray(roles) && roles.every((role) => typeof role === 'string'),
  );
}

/**
 * Authority readers: a present but malformed persisted row is indeterminate and
 * must refuse, never be repaired into an empty (permissive) denial map.
 */
export function parsePersistedSettingsDelegationForAuthority(
  visibleKeys: unknown,
): SettingsDelegationPreference {
  if (visibleKeys === null || visibleKeys === undefined) return emptySettingsDelegationPreference();
  if (
    !isPlainRecord(visibleKeys) ||
    !isWellFormedRoleMap(visibleKeys.grantedRolesByCluster) ||
    !isWellFormedRoleMap(visibleKeys.deniedRolesByCluster)
  )
    throw new Error('Settings permission is unavailable.');
  return parseSettingsDelegationPreference(visibleKeys);
}

export function buildSettingsDelegationPayload(
  preference: SettingsDelegationPreference,
): Record<string, unknown> {
  return {
    grantedRolesByCluster: preference.grantedRolesByCluster,
    deniedRolesByCluster: preference.deniedRolesByCluster,
  };
}

export function normaliseSettingsDelegationPreference(
  preference: SettingsDelegationPreference,
): SettingsDelegationPreference {
  return parseSettingsDelegationPreference({
    grantedRolesByCluster: preference.grantedRolesByCluster,
    deniedRolesByCluster: preference.deniedRolesByCluster,
  });
}

function sanitiseDelegationMap(
  input: Partial<Record<string, string[]>>,
): Partial<Record<string, string[]>> {
  const out: Partial<Record<string, string[]>> = {};
  for (const [clusterId, roles] of Object.entries(input)) {
    if (!DELEGATABLE_ROLES_BY_CLUSTER[clusterId] || !Array.isArray(roles)) continue;
    const allowed = new Set(DELEGATABLE_ROLES_BY_CLUSTER[clusterId].map((r) => r.value));
    const normalised = [...new Set(roles.filter((role) => allowed.has(role)))];
    if (normalised.length > 0) out[clusterId] = normalised;
  }
  return out;
}

export function applyDelegationToRoleList(
  baseRoles: readonly string[],
  clusterId: string | null | undefined,
  delegation: SettingsDelegationPreference | null | undefined,
): readonly string[] {
  if (!clusterId || !delegation) return baseRoles;
  const denies = new Set(delegation.deniedRolesByCluster[clusterId] ?? []);
  const withoutDenials = baseRoles.filter((role) => ADMIN_ROLES.has(role) || !denies.has(role));
  if (isOperatorOnlyCohort(baseRoles)) return withoutDenials;

  const grants = delegation.grantedRolesByCluster[clusterId] ?? [];
  return [...new Set([...withoutDenials, ...grants.filter((role) => !denies.has(role))])];
}

export function isSettingsLeafAllowedForRole(
  leaf: Pick<SettingsLeaf, 'href' | 'roles'>,
  viewerRole: string | null | undefined,
  delegation: SettingsDelegationPreference | null | undefined,
): boolean {
  if (isSettingsDeferredOwnerPath(leaf.href))
    return mainOwnerSettingsLeafAllowedForRole(leaf, viewerRole, delegation);
  return resolveSettingsAccess({ leafId: leaf.href, actor: { role: viewerRole }, delegation })
    .visible;
}

export function allDelegationClusterOptions(): ReadonlyArray<{
  clusterId: string;
  clusterTitle: string;
  roles: readonly { value: string; label: string }[];
}> {
  const titles = new Map(flattenSettingsIa().map((leaf) => [leaf.clusterId, leaf.clusterTitle]));
  return Object.entries(DELEGATABLE_ROLES_BY_CLUSTER)
    .filter(([, roles]) => roles.length > 0)
    .map(([clusterId, roles]) => ({
      clusterId,
      clusterTitle: titles.get(clusterId) ?? clusterId,
      roles,
    }));
}
